import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { nibApi } from '@services/api';
import type {
  NIBSubmissionDetail,
  NIBEmployeeRecord,
  NIBRecordCreateRequest,
  NIBValidationResult,
  NIBSubmission,
  NIBImportResult,
  NIBMergeResult,
  NIBSourceSystem,
  NIBPayIndicator,
} from '@/types';

interface UseNIBSubmissionReturn {
  submission: NIBSubmissionDetail | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  addRecord: (
    data: NIBRecordCreateRequest
  ) => Promise<NIBEmployeeRecord | undefined>;
  updateRecord: (
    recordId: string,
    data: Partial<NIBRecordCreateRequest>
  ) => Promise<NIBEmployeeRecord | undefined>;
  deleteRecord: (recordId: string) => Promise<void>;
  importRecords: (
    file: File,
    sourceSystem: NIBSourceSystem,
    payIndicator?: NIBPayIndicator
  ) => Promise<NIBImportResult | undefined>;
  mergePayroll: (
    file: File,
    sourceSystem: NIBSourceSystem,
    payIndicator?: NIBPayIndicator
  ) => Promise<NIBMergeResult | undefined>;
  validate: () => Promise<NIBValidationResult | undefined>;
  generate: () => Promise<NIBSubmission | undefined>;
  download: () => Promise<void>;
}

/**
 * Custom hook for managing NIB submission operations
 *
 * Encapsulates all submission-level operations including:
 * - Fetching submission detail
 * - Adding/updating/deleting records
 * - Importing records from files
 * - Validation and C10 generation
 * - Downloading generated files
 *
 * Automatically refreshes submission data after operations
 * Shows toast notifications for user feedback
 *
 * @param submissionId - The ID of the NIB submission to manage
 * @returns Object with submission data and operation functions
 */
export function useNIBSubmission(submissionId: string): UseNIBSubmissionReturn {
  const [submission, setSubmission] = useState<NIBSubmissionDetail | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch submission details
  const fetchSubmission = useCallback(async () => {
    if (!submissionId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await nibApi.getSubmissionById(submissionId);
      setSubmission(data);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to fetch submission');
      setError(error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [submissionId]);

  // Refresh submission data
  const refresh = useCallback(async () => {
    await fetchSubmission();
  }, [fetchSubmission]);

  // Add a new record to the submission
  const addRecord = useCallback(
    async (
      data: NIBRecordCreateRequest
    ): Promise<NIBEmployeeRecord | undefined> => {
      if (!submissionId) return;

      try {
        const record = await nibApi.addRecord(submissionId, data);
        toast.success('Record added successfully');
        await refresh();
        return record;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to add record');
        toast.error(error.message);
        throw error;
      }
    },
    [submissionId, refresh]
  );

  // Update an existing record
  const updateRecord = useCallback(
    async (
      recordId: string,
      data: Partial<NIBRecordCreateRequest>
    ): Promise<NIBEmployeeRecord | undefined> => {
      if (!submissionId) return;

      try {
        const record = await nibApi.updateRecord(submissionId, recordId, data);
        toast.success('Record updated successfully');
        await refresh();
        return record;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to update record');
        toast.error(error.message);
        throw error;
      }
    },
    [submissionId, refresh]
  );

  // Delete a record
  const deleteRecord = useCallback(
    async (recordId: string): Promise<void> => {
      if (!submissionId) return;

      try {
        await nibApi.deleteRecord(submissionId, recordId);
        toast.success('Record deleted successfully');
        await refresh();
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to delete record');
        toast.error(error.message);
        throw error;
      }
    },
    [submissionId, refresh]
  );

  // Import records from a file
  const importRecords = useCallback(
    async (
      file: File,
      sourceSystem: NIBSourceSystem,
      payIndicator?: NIBPayIndicator
    ): Promise<NIBImportResult | undefined> => {
      if (!submissionId) return;

      try {
        const result = await nibApi.importRecords(
          submissionId,
          file,
          sourceSystem,
          payIndicator
        );

        toast.success(`Successfully imported ${result.imported_count} records`);

        if (result.skipped_count > 0) {
          toast.error(`${result.skipped_count} records were skipped`, {
            icon: '⚠️',
          });
        }

        await refresh();
        return result;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to import records');
        toast.error(error.message);
        throw error;
      }
    },
    [submissionId, refresh]
  );

  // Merge payroll wages into existing roster records
  const mergePayroll = useCallback(
    async (
      file: File,
      sourceSystem: NIBSourceSystem,
      payIndicator?: NIBPayIndicator
    ): Promise<NIBMergeResult | undefined> => {
      if (!submissionId) return;

      try {
        const result = await nibApi.mergePayroll(
          submissionId,
          file,
          sourceSystem,
          payIndicator
        );

        toast.success(
          `Merged wages for ${result.matched} employees` +
            (result.new_hires.length > 0
              ? `, ${result.new_hires.length} new hires found`
              : '')
        );

        if (result.missing_from_file.length > 0) {
          toast.error(
            `${result.missing_from_file.length} roster employees not found in payroll file`,
            { icon: '\u26A0\uFE0F' }
          );
        }

        // Don't refresh here — let the caller control refresh timing
        // so the modal can display merge results before re-render
        return result;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to merge payroll');
        toast.error(error.message);
        throw error;
      }
    },
    [submissionId]
  );

  // Validate the submission
  const validate = useCallback(async (): Promise<
    NIBValidationResult | undefined
  > => {
    if (!submissionId) return;

    try {
      const result = await nibApi.validateSubmission(submissionId);

      if (result.is_valid) {
        toast.success('Validation successful - ready to generate C10');
      } else {
        toast.error(
          `Validation found ${result.total_errors} error(s) in ${result.invalid_records} record(s)`,
          { icon: '⚠️' }
        );
      }

      await refresh();
      return result;
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to validate submission');
      toast.error(error.message);
      throw error;
    }
  }, [submissionId, refresh]);

  // Generate C10 file
  const generate = useCallback(async (): Promise<NIBSubmission | undefined> => {
    if (!submissionId) return;

    try {
      const result = await nibApi.generateC10(submissionId);
      toast.success('C10 file generated successfully');
      await refresh();
      return result;
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to generate C10 file');
      toast.error(error.message);
      throw error;
    }
  }, [submissionId, refresh]);

  // Download the generated C10 file
  const download = useCallback(async (): Promise<void> => {
    if (!submissionId || !submission) return;

    try {
      const blob = await nibApi.downloadC10(submissionId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename: C10_EMPLOYERNUMBER_MMYY.txt
      const filename = `C10_${submission.employer_number}_${submission.contribution_period}.txt`;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('C10 file downloaded successfully');
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to download C10 file');
      toast.error(error.message);
      throw error;
    }
  }, [submissionId, submission]);

  // Fetch submission on mount and when ID changes
  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  return {
    submission,
    isLoading,
    error,
    refresh,
    addRecord,
    updateRecord,
    deleteRecord,
    importRecords,
    mergePayroll,
    validate,
    generate,
    download,
  };
}

export default useNIBSubmission;
